import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Bot, Key, Plus, Trash2, RefreshCw, Info } from "lucide-react";

const APPROVED_TOPICS = [
  "house_washing", "roof_soft_washing", "driveways_sidewalks",
  "decks_fences_wood_brightening", "gutter_cleaning_whitening",
  "oxidation_removal", "solar_panels",
  "rv_camper_5thwheel_exterior_detailing", "wet_ceramic_protection",
  "commercial_flatwork", "deck_staining_restoration",
  "soft_washing_general",
];

/**
 * Website Sales Agent (Turn 1) — Preview only.
 *
 * Owner/admin-only panel that manages the agent settings, tool API keys
 * (scope `website_sales_agent`), and audit log for the AI assistant embedded
 * on the tenant's marketing website.
 */
export default function WebsiteSalesAgentPanel() {
  const [cfg, setCfg] = useState(null);
  const [services, setServices] = useState([]);
  const [keys, setKeys] = useState([]);
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newKeyName, setNewKeyName] = useState("Rock River Website");
  const [freshKey, setFreshKey] = useState(null);

  const load = async () => {
    try {
      const [s, sv, ks, au] = await Promise.all([
        api.get("/agent/settings"),
        api.get("/services"),
        api.get("/api-keys"),
        api.get("/agent/audit?limit=50"),
      ]);
      setCfg(s.data);
      setServices(sv.data || []);
      setKeys(ks.data?.api_keys || []);
      setAudit(au.data?.events || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load agent settings");
    }
  };
  useEffect(() => { load(); }, []);

  const patch = async (updates) => {
    setBusy(true);
    try {
      const { data } = await api.put("/agent/settings", updates);
      setCfg(data);
      toast.success("Agent settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const createKey = async () => {
    if (!newKeyName.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/api-keys", {
        name: newKeyName.trim(),
        scopes: ["website_sales_agent"],
      });
      setFreshKey(data);
      toast.success("Tool key created — shown once, copy it now.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Key create failed");
    } finally { setBusy(false); }
  };

  const revokeKey = async (id) => {
    if (!confirm("Revoke this tool key? The website assistant will stop working.")) return;
    setBusy(true);
    try {
      await api.post(`/api-keys/${id}/revoke`);
      toast.success("Key revoked");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revoke failed");
    } finally { setBusy(false); }
  };

  if (!cfg) return (
    <div className="bg-white border border-zinc-200 rounded-xl card-crisp p-6 mb-8 text-sm text-zinc-500" data-testid="agent-panel-loading">
      Loading Website Sales Agent…
    </div>
  );

  const agentKeys = keys.filter(k => (k.scopes || []).includes("website_sales_agent"));

  return (
    <div className="bg-white border border-zinc-200 rounded-xl card-crisp p-6 mb-8" data-testid="website-sales-agent-panel">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center shrink-0">
          <Bot className="w-5 h-5 text-[#FF5A00]"/>
        </div>
        <div className="flex-1">
          <h2 className="font-heading text-xl font-bold">Website Sales Agent</h2>
          <p className="text-sm text-zinc-500 mt-1">
            Server-to-server tools for the OpenAI function-calling assistant on your marketing site. Preview only.
          </p>
        </div>
        <span
          className={`text-[10px] uppercase tracking-widest font-mono-accent px-2 py-1 rounded shrink-0 ${
            cfg.enabled ? "bg-emerald-100 text-emerald-800" : "bg-zinc-200 text-zinc-700"
          }`}
          data-testid="agent-status-badge"
        >
          {cfg.enabled ? "Enabled" : "Disabled"} · {cfg.environment}
        </span>
      </div>

      {/* Enable toggle */}
      <div className="mb-6 flex items-center justify-between border border-zinc-200 rounded-lg p-4">
        <div>
          <div className="font-medium">Enable Website Sales Agent</div>
          <p className="text-xs text-zinc-500 mt-1">
            When off, every tool call is fail-closed with HTTP 423. Turn on only after review.
          </p>
        </div>
        <button
          type="button"
          onClick={() => patch({ enabled: !cfg.enabled })}
          disabled={busy}
          className={`inline-flex items-center rounded-full px-4 h-9 text-sm font-medium border transition-colors ${
            cfg.enabled ? "bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-600" : "bg-zinc-100 text-zinc-500 border-zinc-200"
          }`}
          data-testid="agent-enable-toggle"
        >
          {cfg.enabled ? "Enabled" : "Enable"}
        </button>
      </div>

      {/* Tool keys */}
      <div className="mb-6 border border-zinc-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Key className="w-4 h-4 text-zinc-500"/>
          <div className="font-medium">Tool API keys (scope: <code className="bg-zinc-100 px-1 rounded">website_sales_agent</code>)</div>
        </div>
        {freshKey && (
          <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm" data-testid="agent-fresh-key">
            <div className="font-semibold text-amber-900 mb-1">New key — copy it now, it will not be shown again.</div>
            <code className="block break-all bg-white border border-amber-300 rounded p-2 font-mono-accent text-xs">
              {freshKey.key}
            </code>
            <button className="mt-2 text-xs underline" onClick={() => setFreshKey(null)}>Dismiss</button>
          </div>
        )}
        <div className="flex gap-2 mb-3">
          <Input value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)} placeholder="Key name" data-testid="agent-new-key-name"/>
          <Button onClick={createKey} disabled={busy} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white" data-testid="agent-create-key-btn">
            <Plus className="w-4 h-4 mr-1"/> Create key
          </Button>
        </div>
        <div className="space-y-1">
          {agentKeys.length === 0 && <div className="text-xs text-zinc-400">No agent keys yet.</div>}
          {agentKeys.map(k => (
            <div key={k.id} className="flex items-center justify-between text-sm py-1" data-testid={`agent-key-${k.prefix}`}>
              <div className="flex items-center gap-2 min-w-0">
                <code className="font-mono-accent bg-zinc-100 px-2 py-0.5 rounded text-xs">{k.prefix}…</code>
                <span className="truncate">{k.name}</span>
                {k.revoked_at && <span className="text-red-600 text-xs">revoked</span>}
                <span className="text-xs text-zinc-400">last used: {k.last_used_at || "never"}</span>
              </div>
              {!k.revoked_at && (
                <button onClick={() => revokeKey(k.id)} className="text-xs text-red-600 hover:underline" data-testid={`agent-revoke-${k.prefix}`}>
                  <Trash2 className="w-3.5 h-3.5 inline mr-1"/>Revoke
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Coverage */}
      <div className="mb-6 grid md:grid-cols-2 gap-4">
        <div>
          <Label>Service area radius (miles from Sterling, IL)</Label>
          <Input type="number" min={1} max={300}
                 defaultValue={cfg.service_area_radius_miles}
                 onBlur={(e) => {
                   const v = parseInt(e.target.value, 10);
                   if (!isNaN(v) && v !== cfg.service_area_radius_miles) patch({ service_area_radius_miles: v });
                 }}
                 data-testid="agent-radius-input"/>
          {(cfg.review_conflicts || []).length > 0 && (
            <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900 flex items-start gap-1">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0"/>
              Review needed: {cfg.review_conflicts.join(", ")}
            </div>
          )}
        </div>
        <div>
          <Label>Max advance booking days</Label>
          <Input type="number" min={1} max={180}
                 defaultValue={cfg.max_advance_days}
                 onBlur={(e) => {
                   const v = parseInt(e.target.value, 10);
                   if (!isNaN(v) && v !== cfg.max_advance_days) patch({ max_advance_days: v });
                 }}
                 data-testid="agent-max-advance-input"/>
        </div>
        <div>
          <Label>Minimum job value ($)</Label>
          <Input type="number" min={0}
                 defaultValue={cfg.min_job_value}
                 onBlur={(e) => {
                   const v = parseFloat(e.target.value);
                   if (!isNaN(v) && v !== cfg.min_job_value) patch({ min_job_value: v });
                 }}
                 data-testid="agent-min-job-input"/>
        </div>
        <div>
          <Label>Allowed ZIPs (comma-separated, blank = all in radius)</Label>
          <Input defaultValue={(cfg.allowed_zips || []).join(", ")}
                 onBlur={(e) => {
                   const list = e.target.value.split(",").map(s => s.trim()).filter(Boolean);
                   patch({ allowed_zips: list });
                 }}
                 data-testid="agent-zips-input"/>
        </div>
      </div>

      {/* Allowed services */}
      <div className="mb-6 border border-zinc-200 rounded-lg p-4">
        <div className="font-medium mb-2">Allowed services</div>
        <p className="text-xs text-zinc-500 mb-2">Blank = every active service is quotable. Otherwise only the ticked ones.</p>
        <div className="grid md:grid-cols-2 gap-1 max-h-56 overflow-auto">
          {services.filter(s => s.active).map(s => {
            const on = (cfg.allowed_service_ids || []).includes(s.id);
            return (
              <label key={s.id} className="flex items-center gap-2 text-sm" data-testid={`agent-svc-${s.id}`}>
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => {
                    const next = on
                      ? (cfg.allowed_service_ids || []).filter(x => x !== s.id)
                      : Array.from(new Set([...(cfg.allowed_service_ids || []), s.id]));
                    patch({ allowed_service_ids: next });
                  }}
                />
                {s.name}
              </label>
            );
          })}
        </div>
      </div>

      {/* Knowledge editor */}
      <div className="mb-6 border border-zinc-200 rounded-lg p-4">
        <div className="font-medium mb-2">Approved knowledge (process / FAQ)</div>
        <p className="text-xs text-zinc-500 mb-3">The AI is only allowed to quote from these. Blank = topic hidden.</p>
        <div className="space-y-3 max-h-96 overflow-auto">
          {APPROVED_TOPICS.map(t => (
            <div key={t}>
              <Label className="text-xs uppercase tracking-wide text-zinc-500">{t.replace(/_/g, " ")}</Label>
              <Textarea
                defaultValue={cfg.knowledge?.[t] || ""}
                onBlur={(e) => {
                  const next = { ...(cfg.knowledge || {}), [t]: e.target.value };
                  if (next[t] !== (cfg.knowledge?.[t] || "")) patch({ knowledge: next });
                }}
                rows={2}
                data-testid={`agent-kb-${t}`}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Audit log */}
      <div className="border border-zinc-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="font-medium">Audit log (latest 50)</div>
          <button onClick={load} className="text-xs text-zinc-500 hover:underline flex items-center gap-1" data-testid="agent-refresh-audit">
            <RefreshCw className="w-3 h-3"/> Refresh
          </button>
        </div>
        <div className="max-h-72 overflow-auto text-xs font-mono-accent" data-testid="agent-audit-list">
          {audit.length === 0 && <div className="text-zinc-400">No events yet.</div>}
          {audit.map(e => (
            <div key={e.id} className={`py-1 border-b border-zinc-100 flex gap-2 ${e.ok ? "" : "text-red-700"}`}>
              <span className="text-zinc-400 shrink-0">{new Date(e.created_at).toLocaleString()}</span>
              <span className="shrink-0">{e.tool}</span>
              <span className="shrink-0">{e.ok ? "ok" : "fail"}</span>
              <span className="truncate">{e.outcome}{e.error ? ` — ${e.error}` : ""}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
